import type { ReactElement } from 'react'
import { BannerPinIcon } from './figmaIcons'
import styles from './LocationBanner.module.css'

/**
 * Props for {@link LocationBanner}.
 */
export interface LocationBannerProps {
  /** Banner title line (e.g. "Find the nearest station"). */
  readonly title: string
  /** CTA pill label (e.g. "Use location"). */
  readonly ctaLabel: string
  /** Called when the banner is activated to start the §14 Location flow. */
  readonly onUseLocation: () => void
  /**
   * `true` while the host is resolving the user's position. Disables the
   * banner so the user cannot fire a second resolve mid-flight.
   */
  readonly resolving?: boolean
}

/**
 * "Find the nearest station" call-to-action shown directly below the
 * search field (spec §3.3).
 *
 * The package is domain-agnostic: it does NOT call the Geolocation API.
 * Activating the banner fires {@link LocationBannerProps.onUseLocation};
 * the host drives the §14 Location flow (permission prompt → fix →
 * `nearby` state, or the "Location Access Required" alert).
 *
 * The whole banner is one tappable `<button>`; the trailing pill is a
 * visual CTA affordance, not a nested control. Carries the spec a11y id
 * `suggester-location-banner`.
 */
export function LocationBanner({
  title,
  ctaLabel,
  onUseLocation,
  resolving = false,
}: LocationBannerProps): ReactElement {
  return (
    <button
      type="button"
      className={styles.banner}
      data-testid="suggester-location-banner"
      disabled={resolving}
      onClick={onUseLocation}
    >
      <span className={styles.pinCircle}>
        {/* Real Figma-exported pin asset (node 6304:15877) — outlined
            orange `#F54900` marker, used 1:1, not hand-drawn. */}
        <BannerPinIcon size={20} />
      </span>
      <span className={styles.title}>{title}</span>
      <span className={styles.cta}>{ctaLabel}</span>
    </button>
  )
}
